import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import VirtualTour from '../VirtualTour/VirtualTour'
import './Gallery.css'

// --- helpery pro hromadný import ---
const importAll = (r) => r.keys().map((k) => ({ key: k, src: r(k) }))

const toNiceAlt = (key) => {
  // např. "./pokoj/IMG_2981.jpg" -> "pokoj / IMG 2981"
  const clean = key.replace(/^.\//, '') // odstranit "./"
  const parts = clean.split('/')
  const file = parts.pop() || ''
  const name = file.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ')
  const folder = parts.join(' / ')
  return [folder, name].filter(Boolean).join(' / ')
}

const Gallery = () => {
  // 1) rekurzivní importy (třetí parametr = true -> recursive)
  const exteriorCtx = useMemo(
    () => require.context('../album/exterier', true, /\.(png|jpe?g|webp)$/i),
    []
  )
  const interiorCtx = useMemo(
    () => require.context('../album/interier', true, /\.(png|jpe?g|webp)$/i),
    []
  )

  // 2) převedeme na jednotný tvar a seřadíme (stabilně podle cesty)
  const exteriorImages = useMemo(() => {
    return importAll(exteriorCtx)
      .sort((a, b) => a.key.localeCompare(b.key, 'cs', { numeric: true }))
      .map((item, i) => ({
        id: `ext-${i}`,
        src: item.src,
        alt: `Exteriér – ${toNiceAlt(item.key)}`,
        category: 'exterior',
      }))
  }, [exteriorCtx])

  const interiorImages = useMemo(() => {
    return importAll(interiorCtx)
      .sort((a, b) => a.key.localeCompare(b.key, 'cs', { numeric: true }))
      .map((item, i) => ({
        id: `int-${i}`,
        src: item.src,
        alt: `Interiér – ${toNiceAlt(item.key)}`,
        category: 'interior',
      }))
  }, [interiorCtx])

  // 3) společné pole
  const images = useMemo(() => [...exteriorImages, ...interiorImages], [exteriorImages, interiorImages])

  const categories = [
    { id: 'all', name: 'Všechny', count: images.length },
    { id: 'exterior', name: 'Exteriér', count: exteriorImages.length },
    { id: 'interior', name: 'Interiér', count: interiorImages.length },
  ]

  const [activeCategory, setActiveCategory] = useState('all')
  const [selectedImage, setSelectedImage] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)

  const filteredImages = useMemo(() => {
    return activeCategory === 'all'
      ? images
      : images.filter((img) => img.category === activeCategory)
  }, [images, activeCategory])

  const openLightbox = (image, index) => {
    setSelectedImage(image)
    setCurrentIndex(index)
  }
  const closeLightbox = () => setSelectedImage(null)
  const nextImage = () => {
    const next = (currentIndex + 1) % filteredImages.length
    setCurrentIndex(next)
    setSelectedImage(filteredImages[next])
  }
  const prevImage = () => {
    const prev = (currentIndex - 1 + filteredImages.length) % filteredImages.length
    setCurrentIndex(prev)
    setSelectedImage(filteredImages[prev])
  }

  return (
    <section className="gallery-section section">
      <div className="container">
        <motion.div
          className="gallery-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h1 className="section-title">Fotogalerie</h1>
          <p className="section-subtitle">Prohlédněte si naše prostory a okolí chaty</p>
        </motion.div>

        <VirtualTour />

        {/* Filtry */}
        <motion.div
          className="gallery-filters"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.name} ({cat.count})
            </button>
          ))}
        </motion.div>

        {/* Grid */}
        <motion.div className="gallery-grid" layout>
          <AnimatePresence>
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                className="gallery-item"
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.25 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => openLightbox(image, index)}
              >
                <div className="gallery-image">
                  <img src={image.src} alt={image.alt} loading="lazy" />
                  
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              className="lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeLightbox}
            >
              <motion.div
                className="lightbox-content"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button className="lightbox-close" onClick={closeLightbox}><FaTimes /></button>
                <button className="lightbox-nav lightbox-prev" onClick={prevImage}><FaChevronLeft /></button>
                <button className="lightbox-nav lightbox-next" onClick={nextImage}><FaChevronRight /></button>

                <div className="lightbox-image">
                  <img src={selectedImage.src} alt={selectedImage.alt} />
                </div>
                <div className="lightbox-info">
                  <p>{currentIndex + 1} z {filteredImages.length}</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}

export default Gallery
